import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface RecordingPing {
  phase: string;
  recorderName: string;
  recorderRole: string;
  createdAt: string;
}

const PING_WINDOW_MS = 5 * 60 * 1000;

/**
 * Watches for a walkaround that's being filmed right now, so the other party
 * gets a live "come open the app" nudge in the chat.
 */
export function useRecordingPing(bookingId: number | null) {
  const [ping, setPing] = useState<RecordingPing | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!bookingId) {
      setPing(null);
      return undefined;
    }

    const check = async () => {
      try {
        const { data, error } = await supabase
          .from("checkin_videos")
          .select("phase, recorder_name, recorder_role, created_at, status")
          .eq("booking_id", bookingId)
          .eq("status", "recording")
          .order("created_at", { ascending: false })
          .limit(1);
        if (error) throw error;
        const row = data?.[0];
        if (!mounted.current) return;
        if (row && Date.now() - new Date(row.created_at).getTime() < PING_WINDOW_MS) {
          setPing({
            phase: row.phase ?? "pickup",
            recorderName: row.recorder_name || "Your host",
            recorderRole: row.recorder_role || "host",
            createdAt: row.created_at,
          });
        } else {
          setPing(null);
        }
      } catch {
        // Silence — the ping simply stays hidden if we can't reach the backend.
      }
    };

    check();
    const timer = window.setInterval(check, 5000);
    return () => window.clearInterval(timer);
  }, [bookingId]);

  return ping;
}